<script setup>
import { computed, onBeforeUnmount, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute } from 'vue-router';
import { LocalStorage } from 'shared/helpers/localStorage';
import { useAdmin } from 'dashboard/composables/useAdmin';
import { usePolicy } from 'dashboard/composables/usePolicy';
import { useMapGetter } from 'dashboard/composables/store';
import { LOCAL_STORAGE_KEYS } from 'dashboard/constants/localStorage';
import {
  WHATS_NEW_VERSION,
  filterWhatsNewSlides,
} from 'dashboard/constants/whatsNew';
import { WEB_ORDER_ACCESS_PERMISSIONS } from 'dashboard/constants/permissions';
import { isOnOnboardingView } from 'v3/helpers/RouteHelper';
import Button from 'dashboard/components-next/button/Button.vue';

const SLIDE_COPY = {
  welcome: 'WELCOME',
  globalSearch: 'GLOBAL_SEARCH',
  ordersContext: 'ORDERS_CONTEXT',
  editOrderWorkspace: 'EDIT_ORDER_WORKSPACE',
  productCards: 'PRODUCT_CARDS',
};

const SLIDE_ICON = {
  welcome: 'i-lucide-sparkles',
  globalSearch: 'i-lucide-search',
  ordersContext: 'i-lucide-layout-list',
  editOrderWorkspace: 'i-lucide-columns-2',
  productCards: 'i-lucide-shopping-cart',
};

const { t } = useI18n();
const route = useRoute();
const { isAdmin } = useAdmin();
const { checkPermissions } = usePolicy();
const currentUser = useMapGetter('getCurrentUser');

const open = ref(false);
const index = ref(0);
const direction = ref('next');

const userId = computed(() => currentUser.value?.id);
const hasOrderAccess = computed(
  () => isAdmin.value || checkPermissions(WEB_ORDER_ACCESS_PERMISSIONS)
);
const hasOwnReports = computed(
  () => isAdmin.value || checkPermissions(['report_own', 'report_manage'])
);
const slides = computed(() =>
  filterWhatsNewSlides({
    isAdmin: isAdmin.value,
    hasOrderAccess: hasOrderAccess.value,
    hasOwnReports: hasOwnReports.value,
  })
);
const currentId = computed(() => slides.value[index.value] || 'welcome');
const copyKey = computed(
  () => `WHATS_NEW.SLIDES.${SLIDE_COPY[currentId.value]}`
);
const isLast = computed(() => index.value >= slides.value.length - 1);
const enterFromClass = computed(() =>
  direction.value === 'next'
    ? 'translate-x-5 opacity-0'
    : '-translate-x-5 opacity-0'
);
const leaveToClass = computed(() =>
  direction.value === 'next'
    ? '-translate-x-5 opacity-0'
    : 'translate-x-5 opacity-0'
);

const readSeen = () =>
  LocalStorage.get(LOCAL_STORAGE_KEYS.WHATS_NEW_SEEN) || {};

const markSeen = () => {
  if (!userId.value) return;
  const seen = readSeen();
  seen[String(userId.value)] = WHATS_NEW_VERSION;
  LocalStorage.set(LOCAL_STORAGE_KEYS.WHATS_NEW_SEEN, seen);
};

const maybeOpen = () => {
  if (!userId.value || !slides.value.length) return;
  if (isOnOnboardingView(route)) return;
  if (readSeen()[String(userId.value)] === WHATS_NEW_VERSION) return;
  index.value = 0;
  direction.value = 'next';
  open.value = true;
};

const dismiss = () => {
  markSeen();
  open.value = false;
};

const goNext = () => {
  if (isLast.value) {
    dismiss();
    return;
  }
  direction.value = 'next';
  index.value += 1;
};

const goBack = () => {
  if (index.value === 0) return;
  direction.value = 'back';
  index.value -= 1;
};

const goTo = slideIndex => {
  if (slideIndex === index.value) return;
  direction.value = slideIndex > index.value ? 'next' : 'back';
  index.value = slideIndex;
};

const onKeydown = event => {
  if (!open.value) return;
  if (event.key === 'Escape') dismiss();
  if (event.key === 'ArrowRight') goNext();
  if (event.key === 'ArrowLeft') goBack();
};

watch([userId, () => route.name], maybeOpen, { immediate: true });
watch(open, isOpen => {
  if (isOpen) window.addEventListener('keydown', onKeydown);
  else window.removeEventListener('keydown', onKeydown);
});
onBeforeUnmount(() => window.removeEventListener('keydown', onKeydown));
</script>

<template>
  <div
    v-if="open"
    class="fixed inset-0 z-[80] flex items-center justify-center bg-n-alpha-6/50 p-4"
    role="dialog"
    aria-modal="true"
    :aria-labelledby="`whats-new-title-${currentId}`"
  >
    <div
      class="w-full max-w-md overflow-hidden rounded-xl border border-n-weak bg-n-background shadow-xl"
    >
      <div
        class="flex items-center justify-between border-b border-n-weak px-5 py-3"
      >
        <p class="text-xs font-medium uppercase tracking-wide text-n-slate-10">
          {{ t('WHATS_NEW.VERSION_LABEL', { version: WHATS_NEW_VERSION }) }}
        </p>
        <button
          type="button"
          class="text-xs font-medium text-n-slate-10 hover:text-n-slate-12"
          @click="dismiss"
        >
          {{ t('WHATS_NEW.SKIP') }}
        </button>
      </div>

      <div class="overflow-hidden px-5 py-6">
        <Transition
          mode="out-in"
          enter-active-class="transition duration-300 ease-out"
          leave-active-class="transition duration-300 ease-in"
          :enter-from-class="enterFromClass"
          enter-to-class="translate-x-0 opacity-100"
          leave-from-class="translate-x-0 opacity-100"
          :leave-to-class="leaveToClass"
        >
          <div :key="currentId" class="min-h-[9.5rem]">
            <span
              class="mb-4 grid size-10 place-items-center rounded-lg bg-n-brand/10 text-n-brand"
              :class="SLIDE_ICON[currentId]"
            />
            <h2
              :id="`whats-new-title-${currentId}`"
              class="text-lg font-semibold text-n-slate-12"
            >
              {{ t(`${copyKey}.TITLE`) }}
            </h2>
            <p class="mt-2 text-sm leading-6 text-n-slate-11">
              {{ t(`${copyKey}.BODY`) }}
            </p>
          </div>
        </Transition>
      </div>

      <div
        class="flex items-center justify-between gap-3 border-t border-n-weak px-5 py-4"
      >
        <div class="flex items-center gap-1.5">
          <button
            v-for="(slideId, slideIndex) in slides"
            :key="slideId"
            type="button"
            class="size-1.5 rounded-full transition"
            :class="
              slideIndex === index
                ? 'bg-n-brand'
                : 'bg-n-slate-7 hover:bg-n-slate-8'
            "
            :aria-label="t(`WHATS_NEW.SLIDES.${SLIDE_COPY[slideId]}.TITLE`)"
            @click="goTo(slideIndex)"
          />
        </div>
        <div class="flex items-center gap-2">
          <Button
            v-if="index > 0"
            :label="t('WHATS_NEW.BACK')"
            color="slate"
            variant="ghost"
            size="sm"
            @click="goBack"
          />
          <Button
            :label="isLast ? t('WHATS_NEW.DONE') : t('WHATS_NEW.NEXT')"
            color="blue"
            size="sm"
            @click="goNext"
          />
        </div>
      </div>
    </div>
  </div>
</template>
